import React from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { namesAreSimilarEnough } from '../utils/businessNameMatching';

const BusinessNameVerificationModal = ({ 
    show, 
    onConfirm, 
    onCancel, 
    hubspotBusinessName, 
    statementBusinessName,
    statements,
    ssnWarning,
    loading 
}) => {
    // Helper function to check for gaps and missing recent months
    const validateStatementContinuity = (statements) => {
        if (!statements || statements.length === 0) return null;

        const warnings = [];

        statements.forEach(account => {
            const stmts = account.statements;
            if (!stmts || stmts.length === 0) return;

            // Parse dates from statement_period
            // Keep month continuity logic aligned with backend month labeling
            // (start_date + 10 days) to avoid boundary false positives.
            const dates = stmts
                .map(s => {
                    if (!s.statement_period?.start_date) return null;
                    const [year, month, day] = s.statement_period.start_date.split('-').map(Number);
                    if (!year || !month || !day) return null;

                    const shifted = new Date(Date.UTC(year, month - 1, day));
                    shifted.setUTCDate(shifted.getUTCDate() + 10);

                    return {
                        year: shifted.getUTCFullYear(),
                        month: shifted.getUTCMonth() + 1,
                        monthStr: s.month
                    };
                })
                .filter(d => d !== null)
                .sort((a, b) => {
                    if (a.year !== b.year) return a.year - b.year;
                    return a.month - b.month;
                });

            if (dates.length === 0) return;

            // Check for gaps between consecutive months
            for (let i = 1; i < dates.length; i++) {
                const prev = dates[i - 1];
                const curr = dates[i];
                
                // Calculate month difference
                const monthDiff = (curr.year - prev.year) * 12 + (curr.month - prev.month);
                
                if (monthDiff > 1) {
                    const missingMonths = monthDiff - 1;
                    warnings.push({
                        type: 'gap',
                        account: account.account_last4,
                        message: `Account ${account.account_last4}: Missing ${missingMonths} month${missingMonths > 1 ? 's' : ''} between ${prev.monthStr} and ${curr.monthStr}`
                    });
                }
            }

            // Check if most recent month is missing (within last 2 months)
            const lastStatement = dates[dates.length - 1];
            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed
            
            // Calculate months from last statement to now
            const monthsSinceLastStmt = (currentYear - lastStatement.year) * 12 + (currentMonth - lastStatement.month);
            
            // Only warn if it's 2+ months old (meaning they're missing at least one full month)
            if (monthsSinceLastStmt > 2) {
                warnings.push({
                    type: 'recent',
                    account: account.account_last4,
                    message: `Account ${account.account_last4}: Most recent statement is ${lastStatement.monthStr}. Consider uploading more recent statements.`
                });
            }
        });

        return warnings.length > 0 ? warnings : null;
    };

    const warnings = !loading ? validateStatementContinuity(statements) : null;
    const hasNameMismatch = !loading &&
        !namesAreSimilarEnough(hubspotBusinessName, statementBusinessName);

    const nameBoxClassName = hasNameMismatch
        ? 'p-2 rounded border border-danger bg-danger-subtle'
        : 'p-2 bg-light rounded border';

    return (
        <Modal show={show} onHide={onCancel} backdrop="static" keyboard={false} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Submission Validation</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-3">Analyzing submission...</p>
                    </div>
                ) : (
                    <>
                        {/* Section 1: Business Name Verification */}
                        <div className="mb-4 pb-4 border-bottom">
                            <h5 className="mb-3" style={{ color: '#0d6efd' }}>
                                1. Business Name Verification
                            </h5>

                            {hasNameMismatch && (
                                <div className="row mb-2">
                                    <div className="col-md-6" />
                                    <div className="col-md-6">
                                        <div
                                            className="name-mismatch-callout text-danger border border-danger bg-danger-subtle rounded px-3 py-1 d-inline-block"
                                            style={{ fontSize: '0.85rem' }}
                                        >
                                            warning: name mismatch
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold mb-1">HubSpot Contact Name</label>
                                    <div className={nameBoxClassName}>
                                        {hubspotBusinessName ? (
                                            <span className={hasNameMismatch ? 'text-danger fw-semibold' : undefined}>
                                                {hubspotBusinessName}
                                            </span>
                                        ) : (
                                            <em className={hasNameMismatch ? 'text-danger' : 'text-muted'}>Not available</em>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold mb-1">Bank Statement Name</label>
                                    <div className={nameBoxClassName}>
                                        {statementBusinessName ? (
                                            <span className={hasNameMismatch ? 'text-danger fw-semibold' : undefined}>
                                                {statementBusinessName}
                                            </span>
                                        ) : (
                                            <em className={hasNameMismatch ? 'text-danger' : 'text-muted'}>Not found</em>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {!statementBusinessName && (
                                <Alert variant="warning" className="mb-0">
                                    <small>⚠️ Could not extract business name from bank statement. Please verify manually.</small>
                                </Alert>
                            )}
                        </div>

                        {/* Section 2: SSN Verification */}
                        <div className="mb-4 pb-4 border-bottom">
                            <h5 className="mb-3" style={{ color: '#0d6efd' }}>
                                2. SSN Verification
                            </h5>
                            
                            {ssnWarning ? (
                                <Alert variant="danger" className="mb-0">
                                    <div className="d-flex align-items-start">
                                        <span className="me-2">❌</span>
                                        <div>
                                            <strong>Invalid SSN Detected</strong>
                                            <div className="mt-1">{ssnWarning}</div>
                                        </div>
                                    </div>
                                </Alert>
                            ) : (
                                <Alert variant="success" className="mb-0">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">✓</span>
                                        <span>SSN validation passed</span>
                                    </div>
                                </Alert>
                            )}
                        </div>

                        {/* Section 3: Bank Statement Verification */}
                        <div>
                            <h5 className="mb-3" style={{ color: '#0d6efd' }}>
                                3. Bank Statement Verification
                            </h5>
                            
                            {statements && statements.length > 0 ? (
                                <>
                                    {statements.map((account, idx) => (
                                        <div key={idx} className="mb-3 p-3 border rounded bg-light">
                                            <div className="fw-bold mb-2 text-primary">
                                                Account •••• {account.account_last4}
                                            </div>
                                            <ul className="mb-0 ps-3" style={{ fontSize: '0.95rem' }}>
                                                {account.statements.map((stmt, sIdx) => (
                                                    <li key={sIdx} className="mb-1">
                                                        <span className="fw-semibold">{stmt.month}</span>
                                                        <span className="text-muted ms-2">({stmt.filename})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}

                                    {/* Statement Warnings */}
                                    {warnings && warnings.length > 0 ? (
                                        <Alert variant="warning" className="mt-3 mb-0">
                                            <div className="d-flex align-items-start">
                                                <span className="me-2">⚠️</span>
                                                <div>
                                                    <strong>Statement Issues Detected</strong>
                                                    <ul className="mb-0 mt-2 ps-3">
                                                        {warnings.map((warning, idx) => (
                                                            <li key={idx}>{warning.message}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </Alert>
                                    ) : (
                                        <Alert variant="success" className="mb-0">
                                            <div className="d-flex align-items-center">
                                                <span className="me-2">✓</span>
                                                <span>Bank statements validated successfully</span>
                                            </div>
                                        </Alert>
                                    )}
                                </>
                            ) : (
                                <Alert variant="info" className="mb-0">
                                    No bank statements to verify
                                </Alert>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={onConfirm} disabled={loading}>
                    Continue with Analysis
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BusinessNameVerificationModal;

